import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { View } from "@amzn/stencil-react-components/layout";
import React, { Fragment, ReactNode, useEffect, useState } from "react";

import { PERMISSIONS } from "@/config";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { UserPermissions } from "@/services/user-permissions/user-permissions";
import { AppSkeleton } from "../AppSkeleton/app-skeleton";

interface AuthLoadingProps {
  children: ReactNode;
}

type AuthState = "loading" | "success" | "unauthorized" | "error";

/**
 * Gates the entire app behind UserPermissions.authorize().
 * Renders a blank page while loading (no spinner — avoids double-loader
 * when navigating from another app). Shows an error message on failure.
 */
const AuthLoading: React.FC<AuthLoadingProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    UserPermissions.current
      .authorize()
      .then(() => {
        if (UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP) || UserPermissions.current.hasPermission(PERMISSIONS.BRAVE_HEART)) {
          setState("success");
        } else {
          setState("unauthorized");
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Authorization failed";
        setErrorMessage(message);
        setState("error");
      });
  }, []);

  if (state === "loading") {
    return <AppSkeleton />;
  }

  if (state === "unauthorized") {
    return (
      <View padding="2rem">
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("errors.notAuthorized")}
        </MessageBanner>
      </View>
    );
  }

  if (state === "error") {
    return (
      <View padding="2rem">
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {errorMessage}
        </MessageBanner>
      </View>
    );
  }

  return <Fragment>{children}</Fragment>;
};

export default AuthLoading;
